/**
 * Account model and arithmetic (FR-ACC-*, FR-CARD-*).
 *
 * Pure: no Firestore, no React. The sign convention and the utilisation maths are the
 * parts most likely to be got wrong quietly, so they live here where they are tested.
 */

import type { DateKey } from './dates';
import { negate, type Paise } from './money';

export type AccountType =
  | 'bank'
  | 'cash'
  | 'credit_card'
  | 'savings_pot'
  | 'investment'
  | 'loan';

export const ACCOUNT_TYPES: AccountType[] = [
  'bank',
  'cash',
  'credit_card',
  'savings_pot',
  'investment',
  'loan',
];

export const TYPE_LABELS: Record<AccountType, string> = {
  bank: 'Bank',
  cash: 'Cash',
  credit_card: 'Credit',
  savings_pot: 'Savings pot',
  investment: 'Investment',
  loan: 'Loan',
};

/**
 * Liabilities hold a negative balance (SCHEMA §11.2).
 *
 * Uniform signs mean every account sums into a net position without a per-type special
 * case — the alternative, positive outstandings with a type check at every call site, is
 * exactly how a sign error slips into a total.
 */
export function isLiability(type: AccountType): boolean {
  return type === 'credit_card' || type === 'loan';
}

export interface Account {
  id: string;
  name: string;
  type: AccountType;
  currency: 'INR' | 'USD';
  archived: boolean;
  openingBalancePaise: Paise;
  openingBalanceDate: DateKey;
  /**
   * Current balance: opening plus every transaction effect since.
   *
   * Denormalised and maintained by `increment()` in the same batch as each transaction
   * (AD-02). Deriving it on read would mean loading every transaction for the account,
   * which is precisely the cost FR-NFR-03 rules out.
   */
  balancePaise: Paise;
  historicalOpeningPaise?: Paise | null;
  minBalancePaise?: Paise | null;
  transactionCount: number;
  sortOrder: number;

  // credit_card only
  creditLimitPaise?: Paise;
  statementDay?: number | null;
  dueDay?: number;
  dueDayIsNextMonth?: boolean;

  // loan only
  loanId?: string;

  /**
   * How the account's value comes back out (FR-SAV-04).
   *
   * `in-kind` for a scheme that redeems in goods rather than money — GRT Pay returns
   * gold. Absent means `cash`, so every existing account keeps behaving as it did.
   * A property rather than a hardcoded slug: a second gold scheme must work without
   * editing `core/savings.ts`.
   */
  redemption?: 'cash' | 'in-kind';

  /** Why a figure is what it is — e.g. an opening position no source states. */
  note?: string | null;
}

export interface LimitChange {
  limitPaise: Paise;
  effectiveFrom: DateKey;
}

/**
 * What is owed on a liability, as a positive number for display.
 *
 * Storage keeps liabilities negative; humans read "outstanding ₹98,333.84", not
 * "balance −₹98,333.84". This is the one place that flip happens.
 */
export function outstandingPaise(balancePaise: Paise): Paise {
  return negate(balancePaise);
}

export function availableCreditPaise(balancePaise: Paise, limitPaise: Paise): Paise {
  return limitPaise - outstandingPaise(balancePaise);
}

/**
 * Utilisation as a percentage, rounded to one decimal.
 *
 * Returns 0 for a zero or missing limit rather than Infinity or NaN: an unset limit is
 * unknown, not infinitely utilised, and a NaN here would render as "NaN%" in the UI.
 */
export function utilisationPercent(balancePaise: Paise, limitPaise: Paise): number {
  if (!limitPaise || limitPaise <= 0) return 0;
  return Math.round((outstandingPaise(balancePaise) / limitPaise) * 1000) / 10;
}

/**
 * The credit limit in force on a given date (FR-CARD-05).
 *
 * Limits change, and utilisation for a past month has to be measured against the limit
 * that applied then — otherwise raising a limit silently rewrites last year's utilisation
 * history. History is a dated series; this picks the latest entry not after `on`.
 */
export function limitAtDate(
  history: LimitChange[],
  on: DateKey,
  fallback: Paise = 0,
): Paise {
  let best: LimitChange | null = null;
  for (const change of history) {
    if (change.effectiveFrom <= on && (!best || change.effectiveFrom > best.effectiveFrom)) {
      best = change;
    }
  }
  return best ? best.limitPaise : fallback;
}

/** FR-ACC-03: an account carrying transactions may only be archived. */
export function canDelete(account: Pick<Account, 'transactionCount'>): boolean {
  return account.transactionCount === 0;
}

/** FR-ACC-09: a floor breach is a visual warning, never a block. */
export function isBelowMinimum(account: Account, balancePaise: Paise): boolean {
  if (account.minBalancePaise == null || isLiability(account.type)) return false;
  return balancePaise < account.minBalancePaise;
}

/** Net position across accounts. Liabilities are already negative, so this is a sum. */
export function netPositionPaise(balances: Paise[]): Paise {
  return balances.reduce((total, value) => total + value, 0);
}

export function sortAccounts(accounts: Account[]): Account[] {
  return [...accounts].sort(
    (a, b) =>
      ACCOUNT_TYPES.indexOf(a.type) - ACCOUNT_TYPES.indexOf(b.type) ||
      a.sortOrder - b.sortOrder ||
      a.name.localeCompare(b.name),
  );
}

export function groupByType(accounts: Account[]): [AccountType, Account[]][] {
  return ACCOUNT_TYPES.map(
    (type) => [type, sortAccounts(accounts.filter((a) => a.type === type))] as [AccountType, Account[]],
  ).filter(([, list]) => list.length > 0);
}
